
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useOrganization } from '@/hooks/useOrganization';
import { Participant } from './useParticipants';
import { mapGenderToLabel } from '@/lib/gender';

interface APIBiometricProfile {
  glycemic_type: string;
  gender: string;
  ethnicity: string;
  height_cm: number;
  weight_kg: number;
  bmi: number;
  age: number;
  waist_cm: number;
  hip_cm: number;
  waist_to_hip_ratio: number;
  is_complete_profile: boolean;
  updated_at: string;
}

interface APIExperimentParticipant {
  user_experiment_id: string;
  experiment_id: string;
  user_id: string;
  tier: string;
  registered_at: string;
  completed_at: string;
  active: boolean;
  biometric_profile: APIBiometricProfile | null;
  diet: string[];
  diet_exclusions: string[];
  user_diets: string[];
}

function mapEthnicityToLabel(ethnicity: string): string {
  const ethnicityMap: Record<string, string> = {
    'AFRICAN': 'African',
    'ASIAN': 'Asian',
    'EUROPEAN': 'European',
    'LATIN_AMERICAN': 'Latin American',
    'MIDDLE_EASTERN': 'Middle Eastern',
    'NORTH_AMERICAN_INDIGENOUS': 'North American Indigenous',
    'OCEANIAN': 'Oceanian',
    'MIXED_HERITAGE': 'Mixed Heritage',
    'PREFER_NOT_TO_ANSWER': 'Prefer not to answer'
  };

  return ethnicityMap[ethnicity] || ethnicity;
}

function mapGlycemicTypeToLabel(glycemicType: string): string {
  const glycemicTypeMap: Record<string, string> = {
    'DEFAULT': 'Non-diabetic',
    'DIABETIC': 'Diabetic',
    'DIABETIC1': 'Type 1 Diabetic',
    'DIABETIC2': 'Type 2 Diabetic',
    'PREDIABETIC': 'Prediabetic'
  };

  return glycemicTypeMap[glycemicType] || glycemicType;
}

function formatParticipant(participant: APIExperimentParticipant): Participant {
  const { biometric_profile } = participant;

  // Determine age group - handle null biometric_profile
  let ageGroup = '-';
  if (biometric_profile?.age) {
    if (biometric_profile.age < 25) ageGroup = '18-24';
    else if (biometric_profile.age < 35) ageGroup = '25-34';
    else if (biometric_profile.age < 45) ageGroup = '35-44';
    else if (biometric_profile.age < 55) ageGroup = '45-54';
    else if (biometric_profile.age < 65) ageGroup = '55-64';
    else ageGroup = '65+';
  }

  // Determine BMI group - handle null biometric_profile
  let bmiGroup = '-';
  if (biometric_profile?.bmi) {
    if (biometric_profile.bmi < 18.5) bmiGroup = 'Underweight';
    else if (biometric_profile.bmi < 25) bmiGroup = 'Normal';
    else if (biometric_profile.bmi < 30) bmiGroup = 'Overweight';
    else bmiGroup = 'Obese';
  }

  // Build tags array
  const tags: string[] = [];

  // Add glycemic type tag if applicable
  const glycemicType = mapGlycemicTypeToLabel(biometric_profile?.glycemic_type || 'DEFAULT');
  if (glycemicType !== 'Non-diabetic') {
    tags.push(glycemicType);
  }

  // Add diet-related tags
  if (participant.diet) {
    tags.push(...participant.diet);
  }

  if (participant.diet_exclusions) {
    tags.push(...participant.diet_exclusions.map(exclusion => `No ${exclusion}`));
  }

  if (participant.user_diets) {
    tags.push(...participant.user_diets);
  }

  return {
    userId: participant.user_id,
    experimentId: participant.experiment_id,
    userExperimentId: participant.user_experiment_id,
    ageGroup,
    gender: mapGenderToLabel(biometric_profile?.gender),
    ethnicity: biometric_profile?.ethnicity ? mapEthnicityToLabel(biometric_profile.ethnicity) : '-',
    bmiGroup,
    glycemicType,
    tags,
    productLogs: 0, // Not provided by API
    tier: participant.tier || (participant.active ? 'Active' : 'Inactive'),
    registeredAt: participant.registered_at || null,
    completedAt: participant.completed_at || null,
  };
}

export const useExperimentParticipants = (experimentId: string) => {
  const { organization } = useOrganization();

  return useQuery({
    queryKey: ['experiment-participants', experimentId, organization?.id],
    queryFn: async (): Promise<Participant[]> => {
      if (!organization?.id || !experimentId) {
        return [];
      }

      // Verify this experiment belongs to the user's organization
      const { data: orgExperiment, error: orgError } = await supabase
        .from('organization_experiments')
        .select('experiment_id')
        .eq('organization_id', organization.id)
        .eq('experiment_id', experimentId)
        .single();

      if (orgError) {
        throw new Error('Experiment not found or access denied');
      }

      if (!orgExperiment) {
        throw new Error('Experiment not found for this organization');
      }

      // Fetch participants from the Tastermonial API
      const { data: apiResponse, error } = await supabase.functions.invoke('tastermonial-api', {
        body: {
          method: 'GET',
          endpoint: `/api/v2/experiments/${experimentId}/participants`,
        },
      });

      if (error) {
        throw new Error(error.message || 'Failed to fetch experiment participants');
      }

      if (apiResponse.status !== 200) {
        throw new Error(`API error: ${apiResponse.statusText || 'Failed to fetch experiment participants'}`);
      }

      const participants = apiResponse.data as APIExperimentParticipant[];
      console.log('Raw participants from API:', participants);

      const formattedParticipants = participants.map(formatParticipant);
      console.log('Formatted participants:', formattedParticipants);

      return formattedParticipants;
    },
    enabled: !!experimentId && !!organization?.id,
  });
};
